import { Check, ChevronDown, ChevronUp, Hourglass } from 'lucide-react';
import { withFeature } from 'flagged';

import { FeatureResponse } from '../../client';
import { FeaturesSortingOption } from '../../models';
import {
  useAppNavigate,
  useRequireFeatureFlagIfProvided,
  useSortedFeaturesWithTeamContext,
  useTeamDocumentTitle,
  useTeam,
  useFeaturesSortingOption,
} from '../../hooks';

import { Money } from '../atoms';
import { QueryResult } from '../molecules';
import { Page } from '../organisms';

export const FeaturesPage = withFeature('features')(function FeaturesPage() {
  const navigate = useAppNavigate();
  const features = useSortedFeaturesWithTeamContext();
  const team = useTeam();

  useTeamDocumentTitle(team.name);

  return (
    <Page title={null}>
      <QueryResult
        query={features}
        renderAs={(data) => <FeaturesList features={data} onFeatureClick={(feature) => navigate.toFeature(feature)} />}
      />
    </Page>
  );
});

// ─── Column definitions ──────────────────────────────────────────────────────

interface ColumnDef {
  key: string;
  label: string;
  sortOption?: FeaturesSortingOption;
  defaultDirection: 'asc' | 'desc';
  gridWidth: string;
  align?: 'left' | 'right' | 'center';
}

const STATUS_COL: ColumnDef = {
  key: 'status',
  label: '',
  defaultDirection: 'asc',
  gridWidth: '1.25rem',
  align: 'center',
};

const FEATURE_COL: ColumnDef = {
  key: 'feature',
  label: 'Feature',
  sortOption: 'id',
  defaultDirection: 'asc',
  gridWidth: '1fr',
  align: 'left',
};

const EARNINGS_COL: ColumnDef = {
  key: 'earnings',
  label: 'Earnings / sec',
  sortOption: 'value',
  defaultDirection: 'desc',
  gridWidth: 'minmax(5rem, auto)',
  align: 'right',
};

const TOTAL_EARNED_COL: ColumnDef = {
  key: 'total_earned',
  label: 'Total earned',
  defaultDirection: 'desc',
  gridWidth: 'minmax(5rem, auto)',
  align: 'right',
};

const SIZE_COL: ColumnDef = {
  key: 'size',
  label: 'Size',
  sortOption: 'size',
  defaultDirection: 'asc',
  gridWidth: 'minmax(4rem, auto)',
  align: 'right',
};

const MY_TASKS_COL: ColumnDef = {
  key: 'my_tasks',
  label: 'My tasks',
  defaultDirection: 'asc',
  gridWidth: 'minmax(5rem, auto)',
  align: 'right',
};

const WEIGHTED_VALUE_COL: ColumnDef = {
  key: 'weighted_value',
  label: 'Weighted value',
  sortOption: 'weighted-value',
  defaultDirection: 'desc',
  gridWidth: 'minmax(6rem, auto)',
  align: 'right',
};

// ─── Sort helpers ────────────────────────────────────────────────────────────

function getReverse(option: FeaturesSortingOption): FeaturesSortingOption {
  if (option.endsWith('-desc')) {
    return option.replace(/-desc$/, '') as FeaturesSortingOption;
  }
  return `${option}-desc` as FeaturesSortingOption;
}

function getBaseOption(option: FeaturesSortingOption): string {
  return option.replace(/-desc$/, '');
}

// ─── ColumnHeader ────────────────────────────────────────────────────────────

interface ColumnHeaderProps {
  column: ColumnDef;
  sortable: boolean;
  currentSort: FeaturesSortingOption;
  onSort: (option: FeaturesSortingOption) => void;
}

function ColumnHeader({ column, sortable, currentSort, onSort }: ColumnHeaderProps) {
  if (!column.label) {
    return <div />;
  }

  const isActive = column.sortOption && getBaseOption(currentSort) === getBaseOption(column.sortOption);
  const isDescending = currentSort.endsWith('-desc');

  const handleClick = () => {
    if (!sortable || !column.sortOption) return;

    if (isActive) {
      // Toggle direction
      onSort(getReverse(currentSort));
    } else {
      // Switch to this column's default direction
      const base = column.sortOption;
      onSort(column.defaultDirection === 'desc' ? base : base);
    }
  };

  const alignClass =
    column.align === 'right'
      ? 'justify-end text-right'
      : column.align === 'center'
        ? 'justify-center text-center'
        : 'justify-start text-left';

  return (
    <div
      className={`flex items-center gap-1 px-1 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide select-none ${alignClass} ${
        sortable && column.sortOption ? 'cursor-pointer hover:text-text-primary transition-colors' : ''
      }`}
      onClick={handleClick}
    >
      <span>{column.label}</span>
      {isActive && (
        <span className="text-text-accent">
          {isDescending ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        </span>
      )}
    </div>
  );
}

// ─── FeaturesList ────────────────────────────────────────────────────────────

interface FeaturesListProps {
  features: FeatureResponse[];
  onFeatureClick: (feature: FeatureResponse) => void;
}

function FeaturesList({ features, onFeatureClick }: FeaturesListProps) {
  const enableSorting = useRequireFeatureFlagIfProvided('allow_feature_sorting');
  const showEarnings = useRequireFeatureFlagIfProvided('show_feature_earnings');
  const showSize = useRequireFeatureFlagIfProvided('show_feature_size');
  const showMyTasks = useRequireFeatureFlagIfProvided('show_number_of_team_tasks');
  const showWeightedValue = useRequireFeatureFlagIfProvided('show_weighted_value');
  const { sortFeaturesBy, setSortFeaturesBy } = useFeaturesSortingOption();

  const totalEarned = features.reduce((total, feature) => total + (feature.current_earnings ?? 0), 0);

  // Build active columns
  const columns: ColumnDef[] = [STATUS_COL, FEATURE_COL];
  if (showEarnings) {
    columns.push(EARNINGS_COL);
    columns.push(TOTAL_EARNED_COL);
  }
  if (showSize) columns.push(SIZE_COL);
  if (showMyTasks) columns.push(MY_TASKS_COL);
  if (showWeightedValue) columns.push(WEIGHTED_VALUE_COL);

  const gridTemplateColumns = columns.map((c) => c.gridWidth).join(' ');

  return (
    <div className="w-full">
      {features.length === 0 && (
        <div className="w-4/5 mx-auto border border-border-default rounded-md p-4">
          <p className="text-center text-text-secondary">There are no features yet</p>
        </div>
      )}
      {totalEarned > 0 && (
        <p className="my-2 text-center">
          Your company has earned <Money colorScheme="dark">{totalEarned}</Money> in total on all the features
          delivered.
        </p>
      )}
      {features.length > 0 && (
        <div className="overflow-x-auto">
          <div className="grid min-w-[400px]" style={{ gridTemplateColumns }}>
            {/* Header row */}
            {columns.map((col) => (
              <ColumnHeader
                key={col.key}
                column={col}
                sortable={!!enableSorting}
                currentSort={sortFeaturesBy}
                onSort={setSortFeaturesBy}
              />
            ))}
            <div className="col-span-full border-b border-border-muted" />

            {/* Data rows */}
            {features.map((feature) => (
              <div key={feature.id} className="contents group">
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className="py-2.5 group-hover:bg-bg-subtle transition-colors cursor-pointer"
                    onClick={() => onFeatureClick(feature)}
                  >
                    <FeatureCell feature={feature} column={col} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FeatureCell ─────────────────────────────────────────────────────────────

interface FeatureCellProps {
  feature: FeatureResponse;
  column: ColumnDef;
}

function FeatureCell({ feature, column }: FeatureCellProps) {
  const status =
    feature.completed_at_s !== null ? 'completed' : feature.started_at_s !== null ? 'in progress' : 'not started';

  const alignClass =
    column.align === 'right'
      ? 'text-right justify-end'
      : column.align === 'center'
        ? 'text-center justify-center'
        : 'text-left';

  switch (column.key) {
    case 'status':
      return (
        <div className={`flex items-center h-full ${alignClass}`}>
          {status === 'completed' && <Check className="w-4 h-4 text-feedback-success" />}
          {status === 'in progress' && <Hourglass className="w-4 h-4 text-feedback-warning" />}
        </div>
      );
    case 'feature':
      return (
        <div
          className={`flex items-center h-full px-1 ${alignClass} ${feature.completed_at_s !== null ? 'line-through' : ''}`}
        >
          <samp className="text-sm">{feature.id}</samp>
        </div>
      );
    case 'earnings':
      return (
        <div className={`flex items-center h-full px-2 ${alignClass}`}>
          <Money colorScheme="light">{feature.earnings_rate}</Money>
        </div>
      );
    case 'total_earned':
      return (
        <div className={`flex items-center h-full px-2 ${alignClass}`}>
          {feature.current_earnings != null && feature.current_earnings > 0 ? (
            <Money colorScheme="dark">{feature.current_earnings}</Money>
          ) : (
            <span className="text-xs text-text-muted">—</span>
          )}
        </div>
      );
    case 'size':
      return (
        <div className={`flex items-center h-full px-2 ${alignClass}`}>
          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full border border-border-muted text-text-accent">
            {feature.size}
          </span>
        </div>
      );
    case 'my_tasks':
      return (
        <div className={`flex items-center h-full px-2 ${alignClass}`}>
          <span
            className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full ${
              feature.pending_tasks > 0
                ? 'bg-feedback-warning/20 text-feedback-warning'
                : 'bg-feedback-success/20 text-feedback-success'
            }`}
          >
            {feature.pending_tasks > 0 ? feature.pending_tasks : '✓'}
          </span>
        </div>
      );
    case 'weighted_value':
      return (
        <div className={`flex items-center h-full px-2 ${alignClass}`}>
          <span className="text-xs font-mono text-text-secondary">{feature.pending_tasks * feature.earnings_rate}</span>
        </div>
      );
    default:
      return null;
  }
}
